/**
 * Projecao do mes corrente no ritmo atual, com meta opcional de liquido.
 *
 * Decisoes nao obvias:
 *  1. O mes e o do CALENDARIO da loja, independente do filtro de periodo, e
 *     o titulo diz o nome do mes para nao haver duvida.
 *  2. A projecao e linear e inteira (`projetar`): realizado x dias do mes /
 *     dias decorridos. O rodape avisa que nao considera sazonalidade. Uma
 *     projecao "inteligente" que ninguem consegue conferir de cabeca e pior
 *     que uma simples que qualquer lojista refaz na calculadora.
 *  3. A meta e de LIQUIDO (o que fica com a empresa), em centavos, guardada
 *     neste navegador por empresa (`cf.meta-mes.<id>`): estado local da
 *     previa, com "Salvo na previa" como feedback. O backend recebe isso como
 *     coluna quando existir.
 *  4. Reais digitados viram centavos por parse de texto, sem float: "25.000,50"
 *     -> 2500050. A divisao por 100 continua acontecendo so no formatador.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { ErrorPanel } from '../../../components/PageState';
import { Badge, Button, Field, Footnote, Input, Modal, ProgressBar, SectionCard } from '../../../components/ui';
import type { AsyncState } from '../../../lib/api';
import { formatBRLFromCents, formatCount, formatRatio } from '../../../lib/format';
import { projetar } from '../lib/comparacao';
import { nomeDoMes } from '../lib/tempo';
import type { FunnelsSummary, MonthProgress } from '../types';
import { Delta } from './Delta';

const SALVO_VISIVEL_MS = 1_800;

function chaveDaMeta(companyId: string): string {
  return `cf.meta-mes.${companyId}`;
}

function lerMeta(companyId: string): number | null {
  try {
    const bruto = window.localStorage.getItem(chaveDaMeta(companyId));
    if (bruto === null) {
      return null;
    }
    const valor = Number(bruto);
    return Number.isSafeInteger(valor) && valor > 0 ? valor : null;
  } catch {
    return null;
  }
}

function guardarMeta(companyId: string, cents: number | null): void {
  try {
    if (cents === null) {
      window.localStorage.removeItem(chaveDaMeta(companyId));
    } else {
      window.localStorage.setItem(chaveDaMeta(companyId), String(cents));
    }
  } catch {
    // Sem armazenamento (modo privado): a meta vale so ate recarregar.
  }
}

/** "25.000,50" -> 2500050. Devolve null para texto vazio, zero ou invalido. */
export function reaisParaCentavos(texto: string): number | null {
  const limpo = texto.replace(/[^\d,]/g, '');
  if (limpo === '') {
    return null;
  }
  const [inteiro = '', fracao = ''] = limpo.split(',');
  const reais = inteiro === '' ? 0 : Number(inteiro);
  const centavos = Number(`${fracao}00`.slice(0, 2));
  if (!Number.isSafeInteger(reais) || !Number.isSafeInteger(centavos)) {
    return null;
  }
  const total = reais * 100 + centavos;
  return total > 0 && Number.isSafeInteger(total) ? total : null;
}

export function ProjecaoDoMes({
  resumo,
  onRetry,
  companyId,
  className = '',
}: {
  readonly resumo: AsyncState<FunnelsSummary>;
  readonly onRetry: () => void;
  readonly companyId: string;
  readonly className?: string;
}): JSX.Element {
  const [meta, setMeta] = useState<number | null>(() => lerMeta(companyId));
  const [editando, setEditando] = useState(false);
  const [salvo, setSalvo] = useState(false);

  useEffect(() => {
    setMeta(lerMeta(companyId));
  }, [companyId]);

  useEffect(() => {
    if (!salvo) {
      return undefined;
    }
    const timer = window.setTimeout(() => setSalvo(false), SALVO_VISIVEL_MS);
    return () => window.clearTimeout(timer);
  }, [salvo]);

  const definirMeta = (cents: number | null): void => {
    guardarMeta(companyId, cents);
    setMeta(cents);
    setEditando(false);
    setSalvo(true);
  };

  const mes = resumo.status === 'success' ? resumo.data.month : null;
  const nome = mes === null ? 'do mês' : nomeDoMes(mes.monthKey);

  return (
    <SectionCard
      id="projecao-do-mes"
      title={mes === null ? 'Projeção do mês' : `Projeção de ${nome}`}
      icon="calendario"
      description="No ritmo dos dias já decorridos, independente do filtro de período."
      aside={
        <>
          <span role="status" className="text-xs">
            {salvo ? (
              <Badge tone="good" icon="check">
                Salvo na prévia
              </Badge>
            ) : null}
          </span>
          <Button variant="secondary" size="sm" icon={meta === null ? 'mais' : 'editar'} onClick={() => setEditando(true)}>
            {meta === null ? 'Definir meta' : 'Alterar meta'}
          </Button>
        </>
      }
      className={className}
    >
      <div className="flex h-full flex-col justify-between gap-4">
        {resumo.status === 'loading' ? <div aria-hidden="true" className="h-40 animate-pulse rounded-xl bg-ink/5" /> : null}
        {resumo.status === 'error' ? <ErrorPanel title="Não foi possível projetar o mês" error={resumo.error} onRetry={onRetry} /> : null}
        {mes === null ? null : <Corpo mes={mes} meta={meta} onRemoverMeta={() => definirMeta(null)} />}
        <Footnote>
          Projeção linear: o realizado até hoje dividido pelos dias decorridos, vezes os dias do mês. Não considera sazonalidade. A meta é de líquido e fica guardada
          neste navegador (prévia).
        </Footnote>
      </div>

      <FormularioDeMeta aberto={editando} nomeDoMes={nome} metaAtual={meta} onFechar={() => setEditando(false)} onSalvar={definirMeta} />
    </SectionCard>
  );
}

function Corpo({ mes, meta, onRemoverMeta }: { readonly mes: MonthProgress; readonly meta: number | null; readonly onRemoverMeta: () => void }): JSX.Element {
  const projBruto = projetar(mes.realizedAmountCents, mes.daysElapsed, mes.daysInMonth);
  const projLiquido = projetar(mes.realizedNetCents, mes.daysElapsed, mes.daysInMonth);
  const projVendas = projetar(mes.realizedCount, mes.daysElapsed, mes.daysInMonth);
  const porDia = mes.daysElapsed > 0 ? Math.round(mes.realizedNetCents / mes.daysElapsed) : 0;
  const restantes = Math.max(0, mes.daysInMonth - mes.daysElapsed);

  if (projLiquido === null || projBruto === null || projVendas === null) {
    return <p className="text-sm text-ink-subtle">O mês acabou de começar: ainda não há ritmo para projetar.</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Fecha o mês em, no ritmo atual</p>
        <p className="mt-0.5 text-3xl font-semibold tracking-tight tabular-nums text-ink">
          <span aria-hidden="true">≈ </span>
          <span className="sr-only">aproximadamente </span>
          {formatBRLFromCents(projLiquido)} <span className="text-base font-medium text-ink-muted">líquido</span>
        </p>
        <p className="mt-1 text-sm text-ink-muted">
          {formatBRLFromCents(projBruto)} bruto · {formatCount(projVendas)} {projVendas === 1 ? 'venda' : 'vendas'} · {formatBRLFromCents(porDia)} líquidos por dia
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-ink-subtle">
          <Delta atual={projLiquido} anterior={mes.previousMonthNetCents} tipo="dinheiro" />
          <span>sobre o mês anterior fechado ({formatBRLFromCents(mes.previousMonthNetCents)} líquidos)</span>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between text-xs text-ink-muted">
          <span>
            Realizado até hoje: <strong className="font-semibold tabular-nums text-ink">{formatBRLFromCents(mes.realizedNetCents)}</strong> líquidos ·{' '}
            {formatCount(mes.realizedCount)} {mes.realizedCount === 1 ? 'venda' : 'vendas'}
          </span>
          <span className="tabular-nums">
            {formatCount(mes.daysElapsed)} de {formatCount(mes.daysInMonth)} dias
          </span>
        </div>
        <ProgressBar value={mes.daysElapsed} max={mes.daysInMonth} tone="neutral" label="Dias do mês decorridos" />
      </div>

      {meta === null ? (
        <p className="text-xs text-ink-subtle">Sem meta definida. Defina uma meta de líquido para ver quanto falta no ritmo atual.</p>
      ) : (
        <div className="flex flex-col gap-1.5 rounded-xl border border-line bg-elevated px-3 py-3">
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
            <span className="text-ink-muted">
              Meta de líquido: <strong className="font-semibold tabular-nums text-ink">{formatBRLFromCents(meta)}</strong> · {formatRatio(Math.min(mes.realizedNetCents, meta), meta)}{' '}
              alcançados
            </span>
            <Button variant="ghost" size="sm" icon="lixeira" onClick={onRemoverMeta} className="-my-1">
              Remover
            </Button>
          </div>
          <ProgressBar value={mes.realizedNetCents} max={meta} tone={projLiquido >= meta ? 'good' : 'warning'} label="Progresso da meta de líquido" />
          <p className="text-xs text-ink-muted">
            {projLiquido >= meta ? (
              <>
                No ritmo atual, fecha <strong className="font-semibold text-status-good">{formatBRLFromCents(projLiquido - meta)}</strong> acima da meta.
              </>
            ) : (
              <>
                No ritmo atual, faltariam <strong className="font-semibold text-ink">{formatBRLFromCents(meta - projLiquido)}</strong> para a meta
                {restantes > 0 ? (
                  <>
                    : seriam {formatBRLFromCents(Math.ceil((meta - mes.realizedNetCents) / restantes))} líquidos por dia nos {formatCount(restantes)} dias que faltam.
                  </>
                ) : (
                  '.'
                )}
              </>
            )}
          </p>
        </div>
      )}
    </div>
  );
}

function FormularioDeMeta({
  aberto,
  nomeDoMes: nome,
  metaAtual,
  onFechar,
  onSalvar,
}: {
  readonly aberto: boolean;
  readonly nomeDoMes: string;
  readonly metaAtual: number | null;
  readonly onFechar: () => void;
  readonly onSalvar: (cents: number) => void;
}): JSX.Element | null {
  const [texto, setTexto] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (aberto) {
      setTexto(metaAtual === null ? '' : formatBRLFromCents(metaAtual).replace(/^R\$\s?/, ''));
      setErro(null);
    }
  }, [aberto, metaAtual]);

  const enviar = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const cents = reaisParaCentavos(texto);
    if (cents === null) {
      setErro('Informe um valor em reais maior que zero, por exemplo 25.000,00.');
      return;
    }
    onSalvar(cents);
  };

  return (
    <Modal
      open={aberto}
      onClose={onFechar}
      title={`Meta de líquido para ${nome}`}
      description="Quanto a empresa quer ver entrando, líquido da comissão, até o fim do mês."
      footer={
        <>
          <Button variant="ghost" onClick={onFechar}>
            Cancelar
          </Button>
          <Button type="submit" form="form-meta-do-mes" icon="check">
            Salvar meta
          </Button>
        </>
      }
    >
      <form id="form-meta-do-mes" onSubmit={enviar} className="flex flex-col gap-4">
        <Field label="Valor em reais" hint="Só números, vírgula para os centavos. Ex.: 25.000,00" error={erro} required>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              inputMode="decimal"
              autoComplete="off"
              value={texto}
              onChange={(event) => {
                setTexto(event.target.value);
                setErro(null);
              }}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              placeholder="0,00"
            />
          )}
        </Field>
      </form>
    </Modal>
  );
}
